/*
 * DPAPage — Public /dpa page. Explains our Data Privacy Agreement offering,
 * lists what it covers, and lets districts download the template PDF or
 * request a signed / modified version by email.
 */
import { Download, Mail, ShieldCheck, FileText, Lock, Users } from "lucide-react";
import { Link } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DPAPage() {
  useDocumentMeta({
    title: "Data Privacy Agreement (DPA) — Lemonade Stand Business Plan",
    description:
      "Download our K-12 Data Privacy Agreement template. FERPA / COPPA / SOPIPA-aligned. Ready for district review and signature.",
    canonical: "https://learn.lemonadestandbusinessplan.com/dpa",
  });

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-gray-900" data-testid="dpa-page-root">
      <div className="max-w-4xl mx-auto px-5 py-14 sm:py-20">
        {/* Header */}
        <p className="text-xs font-black tracking-[0.28em] uppercase text-yellow-700 mb-3">
          For School &amp; District Partners
        </p>
        <h1
          className="text-[38px] sm:text-5xl lg:text-[64px] font-black tracking-tight leading-[0.98] text-black"
          data-testid="dpa-page-title"
        >
          Data Privacy Agreement
        </h1>
        <p className="mt-4 text-base md:text-xl text-gray-700 max-w-2xl leading-relaxed">
          Our DPA is designed to make district review and procurement fast. It aligns with FERPA,
          COPPA, and major state student-privacy laws (SOPIPA, NY Ed. Law 2-D, CA AB 1584,
          Nevada 388-497, and others).
        </p>

        {/* Primary CTAs */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href={`${BACKEND_URL}/api/public/dpa.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="dpa-download-btn"
            className="inline-flex items-center justify-center gap-2 bg-black text-white font-black rounded-full px-6 py-4 text-lg hover:bg-yellow-400 hover:text-black transition-colors"
          >
            <Download className="w-5 h-5" /> Download DPA Template (PDF)
          </a>
          <a
            href="mailto:privacy@lemonadestandbusinessplan.com?subject=DPA%20request"
            data-testid="dpa-email-btn"
            className="inline-flex items-center justify-center gap-2 bg-white ring-2 ring-black text-black font-black rounded-full px-6 py-4 text-lg hover:bg-yellow-400 transition-colors"
          >
            <Mail className="w-5 h-5" /> Request signed / modified DPA
          </a>
        </div>

        {/* What it covers */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FeatureCard
            icon={<ShieldCheck className="w-6 h-6" />}
            title="FERPA-aligned"
            body='Provider operates as a "school official" under 34 CFR 99.31(a)(1). Districts retain control and ownership of all Student Data.'
          />
          <FeatureCard
            icon={<Users className="w-6 h-6" />}
            title="COPPA-safe"
            body="Under-13 users only join via teacher-issued class codes (school-authorized consent). No advertising, no profiling."
          />
          <FeatureCard
            icon={<Lock className="w-6 h-6" />}
            title="Modern security"
            body="TLS 1.2+, bcrypt hashed passwords, JWT sessions, strict-origin CORS, CSP + HSTS, encrypted backups."
          />
          <FeatureCard
            icon={<FileText className="w-6 h-6" />}
            title="Written commitments"
            body="Named subprocessors, 72-hour breach notification, 30-day parent access/correction/deletion, 60-day post-termination return-or-delete."
          />
        </div>

        {/* Scope summary */}
        <section className="mt-14 rounded-3xl bg-white ring-1 ring-black/10 p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl font-black">What Student Data is covered</h2>
          <ul className="mt-4 space-y-2 text-base text-gray-800 leading-relaxed">
            <li>• Student first name (last-initial only on public leaderboards)</li>
            <li>• Teacher-created class codes and enrollment records</li>
            <li>• Lesson responses, quiz attempts, and progress</li>
            <li>• Student email address <em>only if the school enables email authentication</em></li>
          </ul>
          <p className="mt-4 text-sm text-gray-600">
            We do <strong>not</strong> collect SSNs, biometric data, health data, or precise location data. Complete list at{" "}
            <Link to="/student-privacy" className="text-yellow-700 font-bold underline">
              /student-privacy
            </Link>.
          </p>
        </section>

        {/* Next steps */}
        <section className="mt-10 rounded-3xl bg-black text-white p-6 sm:p-8" data-testid="dpa-next-steps">
          <h2 className="text-2xl font-black">Next steps for your district</h2>
          <ol className="mt-4 space-y-3 text-base text-white/90 leading-relaxed">
            <li>
              <span className="font-black text-[#FFD500]">1.</span> Download the DPA template above and share with your data-privacy officer.
            </li>
            <li>
              <span className="font-black text-[#FFD500]">2.</span> If you require a specific state DPA (SDPC National DPA, California, New York, Nevada, etc.), email us — we accept most standard forms.
            </li>
            <li>
              <span className="font-black text-[#FFD500]">3.</span> Return the signed DPA to{" "}
              <a href="mailto:privacy@lemonadestandbusinessplan.com" className="underline text-[#FFD500]">
                privacy@lemonadestandbusinessplan.com
              </a>{" "}
              — we counter-sign within 5 business days.
            </li>
          </ol>
        </section>

        {/* Related links */}
        <div className="mt-10 flex flex-wrap gap-3 text-sm">
          <Link to="/security-and-student-data" className="text-yellow-700 font-bold underline">
            Security &amp; Student Data
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/student-privacy" className="text-yellow-700 font-bold underline">
            Student Data Privacy Policy
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/subprocessors" className="text-yellow-700 font-bold underline">
            Subprocessors
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/privacy" className="text-yellow-700 font-bold underline">
            Privacy Policy
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/terms" className="text-yellow-700 font-bold underline">
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-black/10 p-5 shadow-sm">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-yellow-100 text-yellow-800 mb-3">
        {icon}
      </div>
      <h3 className="text-2xl font-black">{title}</h3>
      <p className="mt-1 text-base text-gray-700 leading-relaxed">{body}</p>
    </div>
  );
}
